import type { ComponentProps } from "react";
import { cn } from "@/lib/utils";

/**
 * The roster and on-site-periods tables from `Visual - Demo.dc.html`.
 *
 * The source builds these out of flex rows inside a bordered card, which gives
 * no row/column semantics at all; this ships real `<table>` markup styled to
 * land on the same pixels. The card shell — 1.5px outline, 13px radius,
 * `overflow:hidden` so the header's `subtle` fill is clipped by the corners,
 * plus the standard card shadow — lives on `Table`'s wrapper.
 *
 * Wide tables (the reports table is `min-width:780px`) scroll horizontally
 * inside the card: pass `className="min-w-[780px]"`.
 */
type TableProps = ComponentProps<"table"> & {
  /** Classes for the bordered card that wraps the scroll area. */
  containerClassName?: string;
};

export function Table({ className, containerClassName, ...props }: TableProps) {
  return (
    <div
      data-slot="table-container"
      className={cn(
        "overflow-hidden rounded-card border-[1.5px] border-border bg-surface shadow-card",
        containerClassName,
      )}
    >
      <div className="overflow-x-auto">
        <table
          data-slot="table"
          className={cn("w-full border-collapse text-left", className)}
          {...props}
        />
      </div>
    </div>
  );
}

export function TableHeader({ className, ...props }: ComponentProps<"thead">) {
  return (
    <thead
      data-slot="table-header"
      className={cn("bg-subtle", className)}
      {...props}
    />
  );
}

export function TableBody({ className, ...props }: ComponentProps<"tbody">) {
  return <tbody data-slot="table-body" className={className} {...props} />;
}

type TableRowProps = ComponentProps<"tr"> & {
  /**
   * Roster rows are buttons and hover to `surface-hover`; the reports table's
   * rows are inert, and pass `interactive={false}`.
   */
  interactive?: boolean;
};

/**
 * The divider is scoped to `tbody` rows so the header keeps its flush top edge
 * — every body row draws its own top rule, which is also what separates the
 * first row from the header.
 */
export function TableRow({
  className,
  interactive = true,
  ...props
}: TableRowProps) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        "[tbody_&]:border-t-[1.5px] [tbody_&]:border-border-inner",
        "transition-colors",
        interactive && "cursor-pointer [tbody_&]:hover:bg-surface-hover",
        className,
      )}
      {...props}
    />
  );
}

export function TableHead({ className, ...props }: ComponentProps<"th">) {
  return (
    <th
      data-slot="table-head"
      scope="col"
      className={cn(
        "whitespace-nowrap px-[18px] py-[11px] align-middle",
        "font-display text-[0.68rem] font-semibold uppercase tracking-[0.08em] text-muted",
        className,
      )}
      {...props}
    />
  );
}

/** `12px 18px` on the roster; the reports table pays `15px 20px` via props. */
export function TableCell({ className, ...props }: ComponentProps<"td">) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        "px-[18px] py-3 align-middle text-[0.9rem] text-ink",
        className,
      )}
      {...props}
    />
  );
}

type TableEmptyProps = ComponentProps<"td"> & {
  /** Must span the full header so the copy centres across the card. */
  colSpan: number;
};

/** The in-card empty state — "No crew match." / "No on-site periods match." */
export function TableEmpty({
  className,
  colSpan,
  children = "No crew match.",
  ...props
}: TableEmptyProps) {
  return (
    <tr data-slot="table-empty">
      <td
        colSpan={colSpan}
        className={cn(
          "border-t-[1.5px] border-border-inner p-[30px]",
          "text-center text-[0.88rem] text-muted",
          className,
        )}
        {...props}
      >
        {children}
      </td>
    </tr>
  );
}

export type { TableProps, TableRowProps, TableEmptyProps };
